import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { displayLabel } from '../../utils/format';
import { ChevronDownIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';

/**
 * WorkspaceSwitcher renders the current tenant in the top bar.
 * Single-tenant users see a read-only context badge. Multi-tenant users can
 * use the same control to switch workspaces.
 * Superadmins see nothing because platform routes are not tenant-scoped.
 *
 * On switch:
 *   - POST /auth/select-tenant
 *   - new JWT bound to the chosen active_tenant_id
 *   - localStorage records the choice as lastTenantId so a reload
 *     restores the same workspace
 *   - navigate through /t/{newTenantId}/get-started; TenantLayout sends an
 *     already-complete workspace onward to its dashboard
 */
const WorkspaceSwitcher = () => {
  const user = useAuthStore((state) => state.user);
  const memberships = useAuthStore((state) => state.memberships) || [];
  const switchTenant = useAuthStore((state) => state.switchTenant);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (!user || user.is_superuser || user.role === 'superadmin') return null;

  const current = memberships.find((m) => m.tenant_id === user.tenant_id);
  const currentName = current?.tenant_name || user.tenant_name || 'Workspace';
  const currentRole = current?.tenant_role || user.tenant_role;
  const canSwitch = Array.isArray(memberships) && memberships.length > 1;

  const onPick = async (tenantId) => {
    if (tenantId === user.tenant_id) {
      setOpen(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await switchTenant(tenantId);
      setOpen(false);
      navigate(`/t/${data.user.tenant_id}/get-started`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not switch workspace');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => canSwitch && setOpen((v) => !v)}
        aria-disabled={!canSwitch}
        title={canSwitch ? 'Switch workspace' : 'Current workspace'}
        className={`flex min-h-[44px] items-center gap-2 px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 ${
          canSwitch
            ? 'hover:bg-gray-50 dark:hover:bg-gray-700'
            : 'cursor-default'
        }`}
      >
        <BuildingOffice2Icon className="w-4 h-4 text-gray-500" />
        <span className="flex flex-col min-w-0 leading-tight text-left">
          <span className="text-xs text-gray-600 dark:text-gray-300">
            Workspace
          </span>
          <span className="font-medium truncate max-w-[180px]">{currentName}</span>
        </span>
        {currentRole && (
          <span className="hidden xl:inline-flex text-xs px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
            {displayLabel(currentRole)}
          </span>
        )}
        {canSwitch && <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
      </button>
      {canSwitch && open && (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 py-1">
          {error && (
            <div className="px-3 py-2 text-xs text-red-600 bg-red-50 border-b border-red-100">{error}</div>
          )}
          {memberships.map((m) => {
            const isCurrent = m.tenant_id === user.tenant_id;
            return (
              <button
                key={m.tenant_id}
                onClick={() => onPick(m.tenant_id)}
                disabled={busy}
                className={`w-full min-h-[44px] text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-gray-700 flex items-center justify-between ${
                  isCurrent ? 'bg-blue-50/50 dark:bg-gray-700/40' : ''
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{m.tenant_name}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-300">{displayLabel(m.tenant_role)}</div>
                </div>
                {isCurrent && (
                  <span className="text-xs text-blue-700 dark:text-blue-300">Current</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WorkspaceSwitcher;
